/**
 * Lightswitch SDK Core Configuration
 * 
 * This module centralizes all endpoint URLs and configuration for the Lightswitch SDK.
 * Pure JavaScript implementation with no framework dependencies.
 */
// Determine if we're in local environment - browser compatible
// Check for NODE_ENV or if we're running on localhost
const isLocal = (typeof process !== 'undefined' && process?.env?.NODE_ENV === 'local') ||
                (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

// For local development - automatically uses localhost:3000 when running locally
const LIGHTSWITCH_UI_BASE = isLocal ? 'http://localhost:3000' : 'https://uis.getlightswitch.com';
const LIGHTSWITCH_API_BASE = isLocal ? 'http://localhost:8000' : 'https://api.getlightswitch.com';

// Global configuration state
let globalConfig = {
  publishableKey: null,
  appSlug: 'default',
  endpoints: null,
  supabaseClient: null
};

/**
 * Function to generate endpoints with dynamic app slug
 * @param {string} appSlug - The app slug to use for endpoint generation
 * @returns {Object} Object containing all endpoints
 */
const createEndpoints = function () {
  let appSlug = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'default';
  return {
    // Frontend UI endpoints
    login: `${LIGHTSWITCH_UI_BASE}/${appSlug}/login`,
    signup: `${LIGHTSWITCH_UI_BASE}/${appSlug}/signup`,
    paywall: `${LIGHTSWITCH_UI_BASE}/${appSlug}/paywall`,
    // Backend API endpoints
    entitlement_check: `${LIGHTSWITCH_API_BASE}/api/v1/sdk/client/entitled`,
    track: `${LIGHTSWITCH_API_BASE}/api/v1/sdk/client/track`,
    customer_portal: `${LIGHTSWITCH_API_BASE}/api/v1/sdk/client/customer-portal`
  };
};

/**
 * Initialize global configuration
 * @param {Object} config - Configuration object
 * @param {string} config.publishableKey - The publishable key for API requests
 * @param {string} config.appSlug - The app slug for endpoint generation
 * @param {Object} [config.supabaseClient] - Optional Supabase client instance
 */
const initConfig = _ref => {
  let {
    publishableKey,
    appSlug = 'default',
    supabaseClient = null
  } = _ref;
  if (!publishableKey) {
    throw new Error('publishableKey is required');
  }
  globalConfig.publishableKey = publishableKey;
  globalConfig.appSlug = appSlug;
  globalConfig.endpoints = createEndpoints(appSlug);
  globalConfig.supabaseClient = supabaseClient;
};

/**
 * Get current configuration
 * @returns {Object} Current global configuration
 */
const getConfig = () => {
  if (!globalConfig.publishableKey) {
    throw new Error('SDK not initialized. Call init() first.');
  }
  return {
    ...globalConfig
  };
};

/**
 * Get endpoints for current app slug
 * @returns {Object} Current endpoints
 */
const getEndpoints = () => {
  if (!globalConfig.endpoints) {
    throw new Error('SDK not initialized. Call init() first.');
  }
  return {
    ...globalConfig.endpoints
  };
};

/**
 * Get Supabase client instance
 * @returns {Object|null} Supabase client or null if not provided
 */
const getSupabaseClient = () => {
  return globalConfig.supabaseClient;
};

// Environment info for debugging
console.log('🔧 Lightswitch SDK Config:', {
  isLocal,
  uiBase: LIGHTSWITCH_UI_BASE,
  apiBase: LIGHTSWITCH_API_BASE,
  hostname: typeof window !== 'undefined' ? window.location?.hostname : 'unknown',
  nodeEnv: typeof process !== 'undefined' ? process.env?.NODE_ENV : 'unknown'
});

export { LIGHTSWITCH_API_BASE, LIGHTSWITCH_UI_BASE, createEndpoints, getConfig, getEndpoints, getSupabaseClient, initConfig };
//# sourceMappingURL=config.js.map
